import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from 'react-apollo';
import gql from 'graphql-tag';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { expValEquals } from '@confluence/feature-experiments';
import { useExperimentalSiteUserPersonalization } from '@confluence/targeting/entry-points/useExperimentalSiteUserPersonalization';

import type {
	useIsNewUserQuery$data,
	useIsNewUserQuery$variables,
} from '../__types__/useIsNewUserQuery';

const NewUserQuery =
	// eslint-disable-next-line graphql-relay-compat/no-experimental-directives -- Read http://go/cfe-no-experimental-directives
	gql`
		query useIsNewUserQuery($cloudId: String!, $userId: String!) @experimental {
			experimentalSiteUserPersonalization(cloudId: $cloudId, userId: $userId) {
				attributes {
					name
					value
				}
			}
		}
	`;

const CONFLUENCE_FIRST_ACTIVE_TRAIT = 'confluence_first_active';

//THE GA Date of USD, any users onboarding on or after this date are considered new users
const NEW_USER_FROM_DATE = new Date('2025-03-24');

export const useIsNewUser = () => {
	const { cloudId, userId } = useSessionData();

	const queryArgs = {
		variables: { cloudId, userId: userId ?? '' },
		skip: !userId || !cloudId,
	};

	// eslint-disable-next-line graphql-client-usage/graphql-generic-types
	const { data, loading, error } = expValEquals(
		'confluence_deprecate_experimental_personalization',
		'isEnabled',
		true,
	)
		? // eslint-disable-next-line react-hooks/rules-of-hooks -- The value of expValEquals() does not change over the app's lifetime
			useExperimentalSiteUserPersonalization(queryArgs)
		: // eslint-disable-next-line react-hooks/rules-of-hooks -- The value of expValEquals() does not change over the app's lifetime
			useQuery<useIsNewUserQuery$data, useIsNewUserQuery$variables>(NewUserQuery, queryArgs);

	if (loading || error) {
		return {
			isNewUser: true,
		};
	}

	const attributes = data?.experimentalSiteUserPersonalization?.attributes;

	const firstActiveValue = attributes?.find(
		({ name }: { name: string }) => name === CONFLUENCE_FIRST_ACTIVE_TRAIT,
	)?.value;

	const firstActiveDate = new Date(firstActiveValue ?? '');
	// Check if the date is invalid. If it is, we assume the user is new
	// and return true to avoid mistakenly showing the onboarding message.
	// Related ticket: https://product-fabric.atlassian.net/browse/USD-698
	const isValidDate = !isNaN(firstActiveDate.getTime());

	if (!isValidDate) {
		return {
			isNewUser: true,
		};
	}

	return {
		isNewUser: Boolean(firstActiveDate >= NEW_USER_FROM_DATE),
	};
};
